import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, Award, BookOpen, TrendingUp, Users, Star } from "lucide-react";

interface StatsCardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  type: 'pd_hours' | 'enrolled' | 'completed' | 'available';
  data: any;
}

export function StatsCardDialog({ open, onOpenChange, title, type, data }: StatsCardDialogProps) {
  const renderContent = () => {
    switch (type) {
      case 'pd_hours':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">
                {data.completed} / {data.required} Hours
              </div>
              <Progress value={(data.completed / data.required) * 100} className="mb-4" />
              <p className="text-muted-foreground">
                {Math.round(((data.completed / data.required) * 100))}% Complete
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Hours Remaining
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {Math.max(0, data.required - data.completed)}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Progress This Year
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    +{data.completed}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Award className="h-4 w-4" />
                Recent Completions
              </h4>
              {data.recentCourses?.length > 0 ? (
                <div className="space-y-2">
                  {data.recentCourses.map((course: any, index: number) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-muted rounded-lg">
                      <div>
                        <p className="font-medium">{course.title}</p>
                        <p className="text-sm text-muted-foreground">
                          Completed: {new Date(course.completion_date).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge variant="secondary">
                        {course.pd_hours || course.duration_hours} hrs
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">
                  No completed courses yet
                </p>
              )}
            </div>
          </div>
        );

      case 'enrolled':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">
                {data.count}
              </div>
              <p className="text-muted-foreground">Active Enrollments</p>
            </div>

            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Current Courses
              </h4>
              {data.courses?.length > 0 ? (
                <div className="space-y-3">
                  {data.courses.map((course: any, index: number) => (
                    <Card key={index}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h5 className="font-medium">{course.title}</h5>
                            <p className="text-sm text-muted-foreground mt-1">
                              {course.description}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {course.duration_hours} hours
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                Enrolled: {new Date(course.enrollment_date).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <Badge variant="outline" className="ml-2">
                            In Progress
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">
                  No active enrollments
                </p>
              )}
            </div>
          </div>
        );

      case 'completed':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">
                {data.count}
              </div>
              <p className="text-muted-foreground">Courses Completed</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Award className="h-4 w-4" />
                    Total PD Hours
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {data.totalHours || 0}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Star className="h-4 w-4" />
                    Completion Rate
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {data.completionRate || 0}%
                  </div>
                </CardContent>
              </Card>
            </div>

            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Recently Completed
              </h4>
              {data.courses?.length > 0 ? (
                <div className="space-y-2">
                  {data.courses.map((course: any, index: number) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-muted rounded-lg">
                      <div>
                        <p className="font-medium">{course.title}</p>
                        <p className="text-sm text-muted-foreground">
                          Completed: {new Date(course.completion_date).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge variant="secondary" className="mb-1">
                          {course.duration_hours} hrs
                        </Badge>
                        <p className="text-xs text-muted-foreground">
                          {course.category || 'General'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">
                  No completed courses yet
                </p>
              )}
            </div>
          </div>
        );

      case 'available':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">
                {data.count}
              </div>
              <p className="text-muted-foreground">Courses Available</p>
            </div>

            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Featured Courses
              </h4>
              {data.courses?.length > 0 ? (
                <div className="space-y-3">
                  {data.courses.slice(0, 5).map((course: any, index: number) => (
                    <Card key={index}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h5 className="font-medium">{course.title}</h5>
                            <p className="text-sm text-muted-foreground mt-1">
                              {course.description}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {course.duration_hours} hours
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                Max: {course.max_participants}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge variant={course.is_online ? "default" : "secondary"} className="mb-1">
                              {course.is_online ? 'Online' : 'In-Person'}
                            </Badge>
                            <p className="text-xs text-muted-foreground">
                              {course.category || 'General'}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">
                  No courses available
                </p>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title} - Details</DialogTitle>
        </DialogHeader>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}